import React, { useState } from 'react'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import Box from 'transfix-ui/core/Box'
import {
  GetRequestForProposalsQueryParams as IRFPQueryParams,
  RequestForProposalFilters as IRFPFilters,
  ShipperApplication,
  useGetRequestForProposals,
  PollGetRequestForProposals,
  RequestForProposal,
  RequestForProposalsResponse,
} from 'apps/marketplace/apiTypes/requestForProposals'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { EditRfpModal } from './EditRfpModal'
import { RequestForProposalPollingTable } from './RequestForProposalPollingTable'
import { RequestForProposalsFilters } from './RequestForProposalsFilters'

interface IRequestForProposalsTabProps {
  showToast: (message: string) => void
}

export const RequestForProposalsTab = ({ showToast }: IRequestForProposalsTabProps) => {
  const [deleteRfpId, setDeleteRfpId] = useState<number | null>(null)
  const [editRfpId, setEditRfpId] = useState<number | null>(null)
  const [editRfpSalesForceId, setEditRfpSalesForceId] = useState<string>('')
  const [editRfpType, setEditRfpType] = useState<string>('')
  const [editRfpShipper, setEditRfpShipper] = useState<ShipperApplication>()

  const [requestParams, setRequestParams] = useState<IRFPQueryParams>({
    filters: {
      currentUser: true,
    },
    pageCount: 0,
  })
  const { data, loading, refetch } = useGetRequestForProposals({
    queryParams: {
      filters: requestParams.filters,
      pageCount: requestParams.pageCount,
    },
  })
  const { requestForProposals = [], totalCount = 0 } = data || {}

  const [displayRfps, setDisplayRfps] = useState<RequestForProposal[]>(requestForProposals)
  const [rfpCount, setRfpCount] = useState<number>(totalCount)

  const setFilters = (filters: Partial<IRFPFilters>) =>
    setRequestParams({
      ...requestParams,
      filters: {
        ...requestParams.filters,
        ...filters,
      },
    })

  const buildTable = (data: RequestForProposalsResponse | null, loading: boolean) => {
    const { requestForProposals = [], totalCount = 0 } = data || {}

    const pageChangeHandler = (newPageNumber: number) =>
      setRequestParams({
        ...requestParams,
        pageCount: newPageNumber - 1,
      })

    const openEditConfirmationHandler = (
      id: number,
      salesForceId: string,
      rfpType: string,
      shipperApplication: ShipperApplication
    ) => {
      setEditRfpId(id)
      setEditRfpSalesForceId(salesForceId)
      setEditRfpType(rfpType)
      setEditRfpShipper(shipperApplication)
    }

    const openDeleteConfirmationHandler = (id: number) => {
      setDeleteRfpId(id)
    }

    if (!loading) {
      if (requestForProposals !== displayRfps && requestForProposals.length > 0) {
        setDisplayRfps(requestForProposals)
        setRfpCount(totalCount)
      }
    }
    return (
      <>
        <RequestForProposalPollingTable
          loading={loading}
          displayRfps={displayRfps}
          rfpCount={rfpCount}
          requestParams={requestParams}
          openEditConfirmationHandler={openEditConfirmationHandler}
          openDeleteConfirmationHandler={openDeleteConfirmationHandler}
          onPageChange={pageChangeHandler}
        />
      </>
    )
  }
  const inlineMessage = 'Data on this page will now update every four seconds'

  return (
    <>
      <Box mb={2}>
        <RequestForProposalsFilters filters={requestParams.filters} setFilters={setFilters} />
      </Box>
      <Box mb={2}>
        <InlineMessage type={INLINE_MESSAGE_VARIANTS.INFO} message={inlineMessage} />
      </Box>
      <PollGetRequestForProposals queryParams={requestParams} interval={4000}>
        {data => buildTable(data, loading)}
      </PollGetRequestForProposals>
      {deleteRfpId && (
        <DeleteConfirmationModal
          rfpId={deleteRfpId}
          openConfirmation={!!deleteRfpId}
          onRefetch={() => {
            refetch()
            showToast('RFP successfully deleted.')
          }}
          onCloseAction={() => setDeleteRfpId(null)}
        />
      )}
      {editRfpId && (
        <EditRfpModal
          rfpId={editRfpId}
          salesforceRfpId={editRfpSalesForceId}
          rfpType={editRfpType}
          shipperApplication={editRfpShipper}
          openConfirmation={!!editRfpId}
          onError={toastMessage => {
            showToast(toastMessage)
          }}
          onRefetch={() => {
            refetch()
            showToast('RFP successfully edited.')
          }}
          onCloseAction={() => setEditRfpId(null)}
        />
      )}
    </>
  )
}
